import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { removeLoadingKey } from '../helpers/common'

export const BillOfMaterialsLoadingKeys = {
  getBillOfMaterials: 'GET_BILL_OF_MATERIALS',
  createBillOfMaterial: 'CREATE_BILL_OF_MATERIALS',
  deleteBillOfMaterial: 'DELETE_BILL_OF_MATERIALS',
};

export const getBillOfMaterialsById = createAsyncThunk(
  'billOfMaterials/getBillOfMaterialsById',
  async ({ id, type, groupId }, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`/api/v1/bill-of-materials`, {
        params: {
          id, 
          type,
          groupId
        }
      });
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to get instructions');
    }
  }
);

export const createBillOfMaterials = createAsyncThunk(
  'billOfMaterials/createBillOfMaterials',
  async ({ creationData }, { rejectWithValue, dispatch }) => {
    try {
        const { id, type, groupId, newBom } = creationData
        const { data } = await axios.post('/api/v1/bill-of-materials', { newBom, type });
        dispatch(getBillOfMaterialsById({ id, type, groupId }));
        return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create instruction');
    }
  }
);

export const deleteBillOfMaterial = createAsyncThunk(
  'billOfMaterials/deleteBillOfMaterial',
  async ({ id, type, groupId, idToDelete }, { rejectWithValue, dispatch }) => {
    try {
      await axios.delete(`/api/v1/bill-of-materials/${idToDelete}`);
      dispatch(getBillOfMaterialsById({ id, type, groupId }));
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete instruction');
    }
  }
);

const billOfMaterialsSlice = createSlice({
  name: 'billOfMaterials',
  initialState: {
    items: [],
    loading: false,
    hasGroupItems: false,
    loadingKeys: [],
    error: null
  },
  reducers: {
    clearBillOfMaterials: (state) => {
      state.items = [];
      state.hasGroupItems = false;
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getBillOfMaterialsById.pending, (state) => {
        state.loading = true;
        state.loadingKeys = [...state.loadingKeys, BillOfMaterialsLoadingKeys.getBillOfMaterials];
      })
      .addCase(getBillOfMaterialsById.fulfilled, (state, action) => {
        state.items = action.payload?.data;
        state.hasGroupItems = action.payload?.hasGroupItems;
        state.loading = false;
        state.loadingKeys = removeLoadingKey(BillOfMaterialsLoadingKeys.getBillOfMaterials, state.loadingKeys);
      })
      .addCase(getBillOfMaterialsById.rejected, (state, action) => {
        state.error = action.error.message;
        state.items = [];
        state.loading = false;
        state.loadingKeys = removeLoadingKey(BillOfMaterialsLoadingKeys.getBillOfMaterials, state.loadingKeys);
      })
      .addCase(createBillOfMaterials.pending, (state) => {
        state.loading = true;
        state.loadingKeys = [...state.loadingKeys, BillOfMaterialsLoadingKeys.createBillOfMaterial];
      })
      .addCase(createBillOfMaterials.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
        state.loadingKeys = removeLoadingKey(BillOfMaterialsLoadingKeys.createBillOfMaterial, state.loadingKeys);
      })
      .addCase(createBillOfMaterials.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.loadingKeys = removeLoadingKey(BillOfMaterialsLoadingKeys.createBillOfMaterial, state.loadingKeys);
      })
      .addCase(deleteBillOfMaterial.pending, (state) => {
        state.loading = true;
        state.loadingKeys = [...state.loadingKeys, BillOfMaterialsLoadingKeys.deleteBillOfMaterial];
      })
      .addCase(deleteBillOfMaterial.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.loadingKeys = removeLoadingKey(BillOfMaterialsLoadingKeys.deleteBillOfMaterial, state.loadingKeys);
      })
      .addCase(deleteBillOfMaterial.rejected, (state, action) => {
        state.error = action.error.message;
        state.loading = false;
        state.loadingKeys = removeLoadingKey(BillOfMaterialsLoadingKeys.deleteBillOfMaterial, state.loadingKeys);
      });
  }
});

export const { clearBillOfMaterials } = billOfMaterialsSlice.actions;
export default billOfMaterialsSlice.reducer; 